import jsPDF from 'jspdf';

export interface ResearchData {
  reports?: {
    overview?: { content?: any };
    demographics?: { content?: any };
    competitor?: { content?: any };
    reviews?: { content?: any };
    discovery?: { content?: any };
    keywords?: { content?: any };
  };
  // Fallback structure
  overview?: any;
  demographics?: any;
  competitor?: any;
  reviews?: any;
  discovery?: any;
  keywords?: any;
}

export interface ProjectData {
  name?: string;
}

export const generateResearchPDF = (displayData: ResearchData, currentProject: ProjectData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 20;
  const margin = 20;
  const lineHeight = 7;
  const sectionSpacing = 15;

  // Helper function to add text with word wrap
  const addText = (text: string, fontSize: number = 10, isBold: boolean = false, color: string = '#000000') => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.setTextColor(color);
    
    const maxWidth = pageWidth - (margin * 2);
    const lines = doc.splitTextToSize(text, maxWidth);
    
    for (const line of lines) {
      if (yPosition > pageHeight - 20) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(line, margin, yPosition);
      yPosition += lineHeight;
    }
    yPosition += 5;
  };

  // Helper function to add section header with better design
  const addSectionHeader = (title: string) => {
    if (yPosition > pageHeight - 40) {
      doc.addPage();
      yPosition = 20;
    }
    yPosition += sectionSpacing;
    
    // Add colored background for section header
    doc.setFillColor(75, 52, 142); // #4A3B8E
    doc.rect(margin - 5, yPosition - 8, pageWidth - (margin * 2) + 10, 12, 'F');
    
    // Add white text on colored background
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(title, margin, yPosition);
    doc.setTextColor(0, 0, 0); // Reset to black
    
    yPosition += 15;
  };

  // Helper function to add subsection header
  const addSubsectionHeader = (title: string) => {
    if (yPosition > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }
    yPosition += 8;
    addText(title, 12, true, '#6B7280');
    yPosition += 3;
  };


  // Title Page
  addText('RESEARCH REPORT', 20, true, '#4A3B8E');
  yPosition += 10;
  addText(currentProject?.name || 'Product Research', 16, true);
  addText(`Generated on: ${new Date().toLocaleDateString()}`, 12);
  addText('Generated by DesignInspire Research Agent', 10, false, '#666666');
  
  // Add page break
  doc.addPage();
  yPosition = 20;

  // 1. OVERVIEW TAB (Actions)
  addSectionHeader('1. ACTIONS & RECOMMENDATIONS');
  
  const overview = displayData?.reports?.overview?.content || displayData?.overview;
  if (overview) {
    
    if (overview.top_suggestions && overview.top_suggestions.length > 0) {
      addSubsectionHeader('Top Suggestions:');
      overview.top_suggestions.forEach((suggestion: any, index: number) => {
        const title = suggestion.suggestion || suggestion.title || suggestion.description || 'Suggestion';
        addText(`${index + 1}. ${title}`, 10, true);
        if (suggestion.implementation) {
          addText(`   Implementation: ${suggestion.implementation}`, 9, false, '#666666');
        }
        if (suggestion.benefit_percentage) {
          addText(`   Benefit: ${Math.round(suggestion.benefit_percentage)}%`, 9, false, '#4A3B8E');
        }
        if (suggestion.priority) {
          addText(`   Priority: ${suggestion.priority}`, 9, false, '#4A3B8E');
        }
        if (suggestion.impact) {
          addText(`   Impact: ${suggestion.impact}`, 9, false, '#4A3B8E');
        }
        yPosition += 3; // Add spacing between suggestions
      });
    } else {
      addText('No specific suggestions available at this time.', 10, false, '#666666');
    }

    // Loved Features (Strengths)
    if (overview.loved_features && overview.loved_features.length > 0) {
      addSubsectionHeader('Loved Features (Strengths):');
      overview.loved_features.forEach((feature: any, index: number) => {
        const featureName = feature.feature || feature.title || feature.description || 'Feature';
        addText(`${index + 1}. ${featureName}`, 10, true);
        if (feature.percentage) {
          addText(`   Satisfaction: ${feature.percentage}%`, 9, false, '#4A3B8E');
        }
        if (feature.description && feature.description !== featureName) {
          addText(`   Description: ${feature.description}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    } else {
      addText('Loved features analysis in progress...', 10, false, '#666666');
    }

    // Purchase Triggers (Areas for Improvement)
    if (overview.purchase_triggers && overview.purchase_triggers.length > 0) {
      addSubsectionHeader('Purchase Triggers (Opportunities):');
      overview.purchase_triggers.forEach((trigger: any, index: number) => {
        const triggerName = trigger.trigger || trigger.title || trigger.description || 'Trigger';
        addText(`${index + 1}. ${triggerName}`, 10, true);
        if (trigger.percentage) {
          addText(`   Impact: ${trigger.percentage}%`, 9, false, '#4A3B8E');
        }
        if (trigger.description && trigger.description !== triggerName) {
          addText(`   Description: ${trigger.description}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    } else {
      addText('Purchase trigger analysis in progress...', 10, false, '#666666');
    }

    // Key Insights
    if (overview.key_insights && overview.key_insights.length > 0) {
      addSubsectionHeader('Key Insights:');
      overview.key_insights.forEach((insight: any, index: number) => {
        const insightText = insight.insight || insight.title || insight.description || 'Insight';
        addText(`${index + 1}. ${insightText}`, 10, true);
        if (insight.impact) {
          addText(`   Impact: ${insight.impact}`, 9, false, '#4A3B8E');
        }
        if (insight.description && insight.description !== insightText) {
          addText(`   Description: ${insight.description}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    }
  } else {
    addText('Overview data is being processed. Please check back later for detailed recommendations.', 10, false, '#666666');
  }

  // 2. DEMOGRAPHICS TAB
  addSectionHeader('2. DEMOGRAPHICS & TARGET AUDIENCE');
  
  const demographics = displayData?.reports?.demographics?.content || displayData?.demographics;
  if (demographics) {
    
    // Customer Segments
    if (demographics.customer_segments && demographics.customer_segments.length > 0) {
      addSubsectionHeader('Customer Segments:');
      demographics.customer_segments.forEach((segment: any, index: number) => {
        const segmentName = segment.segment || segment.name || segment.title || 'Segment';
        addText(`${index + 1}. ${segmentName}`, 10, true);
        if (segment.percentage) {
          addText(`   Percentage: ${segment.percentage}%`, 9, false, '#4A3B8E');
        }
        if (segment.description) {
          addText(`   Description: ${segment.description}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    } else {
      addText('Customer segment analysis in progress...', 10, false, '#666666');
    }

    // Usage Scenarios
    if (demographics.usage_scenarios && demographics.usage_scenarios.length > 0) {
      addSubsectionHeader('Usage Scenarios:');
      demographics.usage_scenarios.forEach((scenario: any, index: number) => {
        const scenarioName = scenario.scenario || scenario.name || scenario.title || 'Scenario';
        addText(`${index + 1}. ${scenarioName}`, 10, true);
        if (scenario.percentage) {
          addText(`   Percentage: ${scenario.percentage}%`, 9, false, '#4A3B8E');
        }
        if (scenario.context) {
          addText(`   Context: ${scenario.context}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    } else {
      addText('Usage scenario analysis in progress...', 10, false, '#666666');
    }

    // Purchase Drivers
    if (demographics.purchase_drivers && demographics.purchase_drivers.length > 0) {
      addSubsectionHeader('Purchase Drivers:');
      demographics.purchase_drivers.forEach((driver: any, index: number) => {
        const driverName = driver.trigger_event || driver.driver || driver.name || 'Driver';
        addText(`${index + 1}. ${driverName}`, 10, true);
        if (driver.percentage) {
          addText(`   Percentage: ${driver.percentage}%`, 9, false, '#4A3B8E');
        }
        if (driver.core_motivation) {
          addText(`   Motivation: ${driver.core_motivation}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    } else {
      addText('Purchase driver analysis in progress...', 10, false, '#666666');
    }

    // Concerns & Objections
    if (demographics.concerns_objections && demographics.concerns_objections.length > 0) {
      addSubsectionHeader('Concerns & Objections:');
      demographics.concerns_objections.forEach((concern: any, index: number) => {
        const concernName = concern.concern || concern.name || concern.title || 'Concern';
        addText(`${index + 1}. ${concernName}`, 10, true);
        if (concern.percentage) {
          addText(`   Percentage: ${concern.percentage}%`, 9, false, '#4A3B8E');
        }
        if (concern.reason) {
          addText(`   Reason: ${concern.reason}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    } else {
      addText('Concerns analysis in progress...', 10, false, '#666666');
    }

    // Core Values
    if (demographics.core_values && demographics.core_values.length > 0) {
      addSubsectionHeader('Core Values:');
      demographics.core_values.forEach((value: any, index: number) => {
        const valueName = value.value || value.name || value.title || 'Value';
        addText(`${index + 1}. ${valueName}`, 10, true);
        if (value.percentage) {
          addText(`   Percentage: ${value.percentage}%`, 9, false, '#4A3B8E');
        }
        if (value.importance) {
          addText(`   Importance: ${value.importance}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    } else {
      addText('Core values analysis in progress...', 10, false, '#666666');
    }
  } else {
    addText('Demographics data is being processed. Please check back later for detailed audience insights.', 10, false, '#666666');
  }

  // 3. COMPETITOR ANALYSIS TAB
  addSectionHeader('3. COMPETITOR ANALYSIS');
  
  const competitor = displayData?.reports?.competitor?.content || displayData?.competitor;
  if (competitor) {
    
    if (competitor.direct_competitors && competitor.direct_competitors.length > 0) {
      addSubsectionHeader('Direct Competitors:');
      competitor.direct_competitors.slice(0, 5).forEach((comp: any, index: number) => {
        addText(`${index + 1}. ${comp.name || comp.title || 'Competitor'}`, 10, true);
        if (comp.price) {
          addText(`   Price: ${comp.price}`, 9, false, '#666666');
        }
        if (comp.rating || comp.average_rating) {
          addText(`   Rating: ${comp.rating || comp.average_rating}`, 9, false, '#666666');
        }
        if (comp.key_features || comp.features) {
          const features = Array.isArray(comp.key_features || comp.features) 
            ? (comp.key_features || comp.features).join(', ')
            : (comp.key_features || comp.features);
          addText(`   Features: ${features}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    } else {
      addText('Direct competitor analysis in progress...', 10, false, '#666666');
    }

    if (competitor.indirect_competitors && competitor.indirect_competitors.length > 0) {
      addSubsectionHeader('Indirect Competitors:');
      competitor.indirect_competitors.slice(0, 5).forEach((comp: any, index: number) => {
        addText(`${index + 1}. ${comp.name || comp.title || 'Competitor'}`, 10, true);
        if (comp.price) {
          addText(`   Price: ${comp.price}`, 9, false, '#666666');
        }
        if (comp.rating || comp.average_rating) {
          addText(`   Rating: ${comp.rating || comp.average_rating}`, 9, false, '#666666');
        }
        if (comp.key_features || comp.features) {
          const features = Array.isArray(comp.key_features || comp.features) 
            ? (comp.key_features || comp.features).join(', ')
            : (comp.key_features || comp.features);
          addText(`   Features: ${features}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    } else {
      addText('Indirect competitor analysis in progress...', 10, false, '#666666');
    }

    if (competitor.market_positioning) {
      addText('Market Positioning:', 12, true);
      addText(JSON.stringify(competitor.market_positioning, null, 2), 10);
    } else {
      addText('Market positioning analysis in progress...', 10, false, '#666666');
    }
  } else {
    addText('Competitor analysis is being processed. Please check back later for detailed competitive insights.', 10, false, '#666666');
  }

  // 4. REVIEWS TAB
  addSectionHeader('4. CUSTOMER REVIEWS & FEEDBACK');
  
  const reviews = displayData?.reports?.reviews?.content || displayData?.reviews;
  if (reviews) {
    
    // Customer Sentiments Summary
    if (reviews.customer_sentiments) {
      const sentiments = reviews.customer_sentiments;
      
      if (sentiments.total_reviews) {
        addSubsectionHeader('Review Summary:');
        addText(`Total Reviews: ${sentiments.total_reviews}`, 10, true);
      }
      
      if (sentiments.average_rating) {
        addText(`Average Rating: ${sentiments.average_rating}/5`, 10, true);
      }
      
      yPosition += 5;
    }

    // Positive Sentiments
    if (reviews.customer_sentiments?.positive_sentiments && reviews.customer_sentiments.positive_sentiments.length > 0) {
      addSubsectionHeader('Positive Reviews:');
      reviews.customer_sentiments.positive_sentiments.forEach((review: any, index: number) => {
        addText(`${index + 1}. ${review.sentiment || 'Positive Review'}`, 10, true);
        if (review.percentage) {
          addText(`   Percentage: ${review.percentage}%`, 9, false, '#4A3B8E');
        }
        if (review.theme) {
          addText(`   Theme: ${review.theme}`, 9, false, '#666666');
        }
        if (review.description) {
          addText(`   Description: ${review.description}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    } else {
      addText('Positive review analysis in progress...', 10, false, '#666666');
    }

    // Negative Sentiments
    if (reviews.customer_sentiments?.negative_sentiments && reviews.customer_sentiments.negative_sentiments.length > 0) {
      addSubsectionHeader('Negative Reviews:');
      reviews.customer_sentiments.negative_sentiments.forEach((review: any, index: number) => {
        addText(`${index + 1}. ${review.sentiment || 'Negative Review'}`, 10, true);
        if (review.percentage) {
          addText(`   Percentage: ${review.percentage}%`, 9, false, '#4A3B8E');
        }
        if (review.theme) {
          addText(`   Theme: ${review.theme}`, 9, false, '#666666');
        }
        if (review.description) {
          addText(`   Description: ${review.description}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    } else {
      addText('Negative review analysis in progress...', 10, false, '#666666');
    }

    // Expectation Gaps
    if (reviews.customer_sentiments?.expectation_gaps && reviews.customer_sentiments.expectation_gaps.length > 0) {
      addSubsectionHeader('Expectation Gaps:');
      reviews.customer_sentiments.expectation_gaps.forEach((gap: any, index: number) => {
        addText(`${index + 1}. ${gap.gap || 'Expectation Gap'}`, 10, true);
        if (gap.frequency) {
          addText(`   Frequency: ${gap.frequency}`, 9, false, '#4A3B8E');
        }
        if (gap.description) {
          addText(`   Description: ${gap.description}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    }

    // Barriers to Purchase
    if (reviews.customer_sentiments?.barriers && reviews.customer_sentiments.barriers.length > 0) {
      addSubsectionHeader('Barriers to Purchase:');
      reviews.customer_sentiments.barriers.forEach((barrier: any, index: number) => {
        addText(`${index + 1}. ${barrier.barrier || 'Purchase Barrier'}`, 10, true);
        if (barrier.type) {
          addText(`   Type: ${barrier.type}`, 9, false, '#4A3B8E');
        }
        if (barrier.description) {
          addText(`   Description: ${barrier.description}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    }

    // Review Patterns
    if (reviews.review_patterns && reviews.review_patterns.length > 0) {
      addSubsectionHeader('Review Patterns:');
      reviews.review_patterns.forEach((pattern: any, index: number) => {
        addText(`${index + 1}. ${pattern.pattern || 'Review Pattern'}`, 10, true);
        if (pattern.frequency) {
          addText(`   Frequency: ${pattern.frequency}`, 9, false, '#4A3B8E');
        }
        if (pattern.description) {
          addText(`   Description: ${pattern.description}`, 9, false, '#666666');
        }
        if (pattern.insight) {
          addText(`   Insight: ${pattern.insight}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    }

    // Sentiment Trends
    if (reviews.sentiment_trends && reviews.sentiment_trends.length > 0) {
      addSubsectionHeader('Sentiment Trends:');
      reviews.sentiment_trends.forEach((trend: any, index: number) => {
        addText(`${index + 1}. ${trend.trend || 'Sentiment Trend'}`, 10, true);
        if (trend.description) {
          addText(`   Description: ${trend.description}`, 9, false, '#666666');
        }
        if (trend.impact) {
          addText(`   Impact: ${trend.impact}`, 9, false, '#4A3B8E');
        }
        yPosition += 3;
      });
    }
  } else {
    addText('Review analysis is being processed. Please check back later for detailed customer feedback insights.', 10, false, '#666666');
  }

  // 5. PRODUCT DISCOVERY TAB
  addSectionHeader('5. PRODUCT DISCOVERY & MARKET INSIGHTS');
  
  const discovery = displayData?.reports?.discovery?.content || displayData?.discovery;
  if (discovery) {
    
    // Product Features
    if (discovery.product_features && discovery.product_features.length > 0) {
      addSubsectionHeader('Product Features:');
      discovery.product_features.forEach((feature: any, index: number) => {
        addText(`${index + 1}. ${feature.feature || 'Product Feature'}`, 10, true);
        if (feature.description) {
          addText(`   Description: ${feature.description}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    }

    // USPs (Unique Selling Propositions)
    if (discovery.usps && discovery.usps.length > 0) {
      addSubsectionHeader('Unique Selling Propositions:');
      discovery.usps.forEach((usp: any, index: number) => {
        addText(`${index + 1}. ${usp.usp || 'USP'}`, 10, true);
        if (usp.customer_value) {
          addText(`   Customer Value: ${usp.customer_value}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    }

    // Functional Benefits
    if (discovery.functional_benefits && discovery.functional_benefits.length > 0) {
      addSubsectionHeader('Functional Benefits:');
      discovery.functional_benefits.forEach((benefit: any, index: number) => {
        addText(`${index + 1}. ${benefit}`, 10, true);
        yPosition += 3;
      });
    }

    // Emotional Benefits
    if (discovery.emotional_benefits && discovery.emotional_benefits.length > 0) {
      addSubsectionHeader('Emotional Benefits:');
      discovery.emotional_benefits.forEach((benefit: any, index: number) => {
        addText(`${index + 1}. ${benefit}`, 10, true);
        yPosition += 3;
      });
    }

    // Brand Information
    if (discovery.brand_background) {
      addSubsectionHeader('Brand Background:');
      addText(discovery.brand_background, 10, false, '#666666');
      yPosition += 5;
    }

    // Package Contents
    if (discovery.package_contents && discovery.package_contents.length > 0) {
      addSubsectionHeader('Package Contents:');
      discovery.package_contents.forEach((item: any, index: number) => {
        addText(`${index + 1}. ${item.item || 'Item'}: ${item.description || 'Description'}`, 10, true);
        yPosition += 3;
      });
    }

    // Ingredients
    if (discovery.ingredients) {
      addSubsectionHeader('Ingredients:');
      addText(discovery.ingredients, 10, false, '#666666');
      yPosition += 5;
    }

    // Variants
    if (discovery.variants && discovery.variants.length > 0) {
      addSubsectionHeader('Product Variants:');
      discovery.variants.forEach((variant: any, index: number) => {
        addText(`${index + 1}. ${variant.variant || variant.name || 'Variant'}`, 10, true);
        if (variant.description) {
          addText(`   Description: ${variant.description}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    }

    // Certifications
    if (discovery.certifications && discovery.certifications.length > 0) {
      addSubsectionHeader('Certifications:');
      discovery.certifications.forEach((cert: any, index: number) => {
        addText(`${index + 1}. ${cert.certification || cert.name || 'Certification'}`, 10, true);
        if (cert.description) {
          addText(`   Description: ${cert.description}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    }

    // Founder Story
    if (discovery.founder_story) {
      addSubsectionHeader('Founder Story:');
      addText(discovery.founder_story, 10, false, '#666666');
      yPosition += 5;
    }
  } else {
    addText('Product discovery analysis is being processed. Please check back later for detailed market insights.', 10, false, '#666666');
  }

  // 6. KEYWORDS TAB
  addSectionHeader('6. KEYWORD ANALYSIS');
  
  const keywords = displayData?.reports?.keywords?.content || displayData?.keywords;
  if (keywords) {
    
    // Access keyword analysis data
    const keywordAnalysis = keywords.keyword_analysis || {};
    
    // Primary Keywords
    if (keywordAnalysis.primary_keywords && keywordAnalysis.primary_keywords.length > 0) {
      addSubsectionHeader('Primary Keywords:');
      keywordAnalysis.primary_keywords.slice(0, 10).forEach((keyword: any, index: number) => {
        addText(`${index + 1}. ${keyword.keyword || 'Keyword'}`, 10, true);
        if (keyword.search_volume) {
          addText(`   Search Volume: ${keyword.search_volume}`, 9, false, '#666666');
        }
        if (keyword.sales_potential) {
          addText(`   Sales Potential: ${keyword.sales_potential}`, 9, false, '#4A3B8E');
        }
        if (keyword.competition) {
          addText(`   Competition: ${keyword.competition}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    } else {
      addText('Primary keyword analysis in progress...', 10, false, '#666666');
    }

    // Long-tail Keywords
    if (keywordAnalysis.long_tail_keywords && keywordAnalysis.long_tail_keywords.length > 0) {
      addSubsectionHeader('Long-tail Keywords:');
      keywordAnalysis.long_tail_keywords.slice(0, 10).forEach((keyword: any, index: number) => {
        addText(`${index + 1}. ${keyword.keyword || 'Keyword'}`, 10, true);
        if (keyword.search_volume) {
          addText(`   Search Volume: ${keyword.search_volume}`, 9, false, '#666666');
        }
        if (keyword.sales_potential) {
          addText(`   Sales Potential: ${keyword.sales_potential}`, 9, false, '#4A3B8E');
        }
        if (keyword.trend) {
          addText(`   Trend: ${keyword.trend}`, 9, false, '#666666');
        }
        if (keyword.competition) {
          addText(`   Competition: ${keyword.competition}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    } else {
      addText('Long-tail keyword analysis in progress...', 10, false, '#666666');
    }

    // Secondary Keywords
    if (keywordAnalysis.secondary_keywords && keywordAnalysis.secondary_keywords.length > 0) {
      addSubsectionHeader('Secondary Keywords:');
      keywordAnalysis.secondary_keywords.slice(0, 10).forEach((keyword: any, index: number) => {
        addText(`${index + 1}. ${keyword.keyword || 'Keyword'}`, 10, true);
        if (keyword.search_volume) {
          addText(`   Search Volume: ${keyword.search_volume}`, 9, false, '#666666');
        }
        if (keyword.sales_potential) {
          addText(`   Sales Potential: ${keyword.sales_potential}`, 9, false, '#4A3B8E');
        }
        if (keyword.competition) {
          addText(`   Competition: ${keyword.competition}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    }

    // Short-tail Keywords
    if (keywordAnalysis.short_tail_keywords && keywordAnalysis.short_tail_keywords.length > 0) {
      addSubsectionHeader('Short-tail Keywords:');
      keywordAnalysis.short_tail_keywords.slice(0, 10).forEach((keyword: any, index: number) => {
        addText(`${index + 1}. ${keyword.keyword || 'Keyword'}`, 10, true);
        if (keyword.search_volume) {
          addText(`   Search Volume: ${keyword.search_volume}`, 9, false, '#666666');
        }
        if (keyword.sales_potential) {
          addText(`   Sales Potential: ${keyword.sales_potential}`, 9, false, '#4A3B8E');
        }
        if (keyword.ranking_difficulty) {
          addText(`   Ranking Difficulty: ${keyword.ranking_difficulty}`, 9, false, '#666666');
        }
        if (keyword.competition) {
          addText(`   Competition: ${keyword.competition}`, 9, false, '#666666');
        }
        yPosition += 3;
      });
    }

    // Competitor Keywords
    if (keywordAnalysis.competitor_keywords && keywordAnalysis.competitor_keywords.length > 0) {
      addSubsectionHeader('Competitor Keywords:');
      keywordAnalysis.competitor_keywords.slice(0, 10).forEach((keyword: any, index: number) => {
        addText(`${index + 1}. ${keyword.keyword || 'Keyword'}`, 10, true);
        if (keyword.competitor) {
          addText(`   Competitor: ${keyword.competitor}`, 9, false, '#666666');
        }
        if (keyword.search_volume) {
          addText(`   Search Volume: ${keyword.search_volume}`, 9, false, '#666666');
        }
        if (keyword.sales_potential) {
          addText(`   Sales Potential: ${keyword.sales_potential}`, 9, false, '#4A3B8E');
        }
        yPosition += 3;
      });
    } else {
      addText('Competitor keyword analysis in progress...', 10, false, '#666666');
    }
  } else {
    addText('Keyword analysis is being processed. Please check back later for detailed keyword insights.', 10, false, '#666666');
  }

  // Footer
  yPosition += 20;
  addText('--- End of Report ---', 10, true, '#666666');
  addText('Generated by DesignInspire Research Agent', 8, false, '#666666');

  return doc;
};

// Generate a simple PDF for Storefront Submission data
export const generateStorefrontSubmissionPDF = (formData: any) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = 20;
  const margin = 20;
  const lineHeight = 7;

  const addText = (text: string, fontSize: number = 10, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(fontSize);
    const maxWidth = pageWidth - margin * 2;
    const lines = doc.splitTextToSize(text, maxWidth);
    for (const line of lines) {
      if (y > pageHeight - 20) {
        doc.addPage();
        y = 20;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    }
    y += 2;
  };

  const addSection = (title: string) => {
    if (y > pageHeight - 30) { doc.addPage(); y = 20; }
    y += 6;
    doc.setFillColor(75, 52, 142);
    doc.rect(margin - 5, y - 8, pageWidth - (margin * 2) + 10, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(title, margin, y);
    doc.setTextColor(0, 0, 0);
    y += 12;
  };

  const field = (label: string, value?: any) => {
    const safe = (value === undefined || value === null || value === '') ? '-' : String(value);
    addText(`${label}: ${safe}`, 10, false);
  };

  const addDownloadLink = (label: string, url?: string, filename?: string) => {
    if (!url) return;
    const left = margin;
    const linkLabel = `${label}:`;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    if (y > pageHeight - 20) { doc.addPage(); y = 20; }
    doc.text(linkLabel, left, y);
    const labelWidth = doc.getTextWidth(linkLabel + ' ');
    const linkText = filename ? `Download ${filename}` : 'Download';
    const x = left + labelWidth;
    // Draw clickable blue link text
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(33, 150, 243);
    doc.text(linkText, x, y);
    const linkWidth = doc.getTextWidth(linkText);
    // Underline
    doc.setDrawColor(33, 150, 243);
    doc.line(x, y + 1, x + linkWidth, y + 1);
    // Clickable area
    doc.link(x, y - 4, linkWidth, 8, { url });
    // Reset
    doc.setTextColor(0, 0, 0);
    y += lineHeight + 2;
  };

  // Title
  addText('Storefront Submission', 20, true);
  addText(`Generated on: ${new Date().toLocaleString()}`, 10);

  addSection('Personal Details');
  field('First Name', formData.firstName);
  field('Last Name', formData.lastName);
  field('Email', formData.email);

  addSection('Company Details');
  field('Brand Name', formData.brandName);
  field('Website', formData.website);
  field('Products & Categories', formData.productCountAndCategoriesFileName ? `File: ${formData.productCountAndCategoriesFileName}` : formData.productCountAndCategories);
  addDownloadLink('Products & Categories File', formData.productCountAndCategoriesFileUrl || (typeof formData.productCountAndCategories === 'string' && formData.productCountAndCategories.startsWith('http') ? formData.productCountAndCategories : undefined), formData.productCountAndCategoriesFileName);
  field('ASINs or Links', formData.productAsinsOrLinksFileName ? `File: ${formData.productAsinsOrLinksFileName}` : formData.productAsinsOrLinks);
  addDownloadLink('ASINs or Links File', formData.productAsinsOrLinksFileUrl || (typeof formData.productAsinsOrLinks === 'string' && formData.productAsinsOrLinks.startsWith('http') ? formData.productAsinsOrLinks : undefined), formData.productAsinsOrLinksFileName);
  field('Best Sellers / Heroes', formData.bestSellersOrHeroes);
  field('Brand Mentions / Media', formData.hasBrandMentions ? 'Yes' : 'No');
  field('Key Features / Benefits', formData.keyFeaturesBenefits);
  field('Target Audience', formData.targetAudience);

  addSection('Competitors');
  field('Competitor 1', formData.competitor1);
  field('Competitor 2', formData.competitor2);
  field('Competitor 3', formData.competitor3);
  field('Closest Competitor', formData.closestCompetitor);
  field('Likes/Dislikes & Differentiation', formData.competitorLikeDislike);
  field('How you compare', formData.brandComparison);

  addSection('Tone & Style');
  field('Brand Tonality', formData.brandTonality);
  field('Inspirations / References', formData.inspirationsReferences);
  field('Desired Design Style', formData.desiredDesignStyle);

  addSection('Favorite Brands');
  field('Brand 1 Link', formData.favBrand1Link);
  field('Brand 1 Describe', formData.favBrand1Describe);
  field('Brand 2 Link', formData.favBrand2Link);
  field('Brand 2 Describe', formData.favBrand2Describe);
  field('Brand 3 Link', formData.favBrand3Link);
  field('Brand 3 Describe', formData.favBrand3Describe);

  return doc;
};
